import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@src/api/config";

export type ContractorDashboardPeriod = "daily" | "weekly" | "monthly" | "yearly";

type DashboardSnapshot = {
  summary: {
    contractorName: string;
    businessLine: string;
    completion: number;
    totalPremium: number;
    averagePremium: number;
    growth: string;
  };
  premiumTrend: {
    labels: string[];
    values: number[];
  };
  certificates: {
    total: number;
    growth: string;
    innerText: string;
    stats: Array<{ label: string; value: number; color: string }>;
  };
  pendingPolicies: {
    amount: number;
    labels: string[];
    pending: number[];
    expired: number[];
  };
  topCompanies: {
    labels: string[];
    values: number[];
  };
};

export interface ContractorDashboardState {
  loading: boolean;
  period: ContractorDashboardPeriod;
  snapshots: Record<ContractorDashboardPeriod, DashboardSnapshot>;
}

const emptyCertificates = {
  total: 0,
  growth: "",
  innerText: "Certificados emitidos",
  stats: [
    { label: "Emitido", value: 0, color: "#00CFE8" },
    { label: "Con factura", value: 0, color: "#7367F0" },
    { label: "Pagado", value: 0, color: "#20C997" },
    { label: "Cancelado", value: 0, color: "#EA5455" },
    { label: "Comisionado", value: 0, color: "#4b4b4b" }
  ]
};

const createEmptyContractorSnapshot = (): DashboardSnapshot => ({
  summary: {
    contractorName: "Contratistas",
    businessLine: "Contratistas",
    completion: 0,
    totalPremium: 0,
    averagePremium: 0,
    growth: ""
  },
  premiumTrend: {
    labels: [],
    values: []
  },
  certificates: emptyCertificates,
  pendingPolicies: {
    amount: 0,
    labels: ["Pólizas"],
    pending: [0],
    expired: [0]
  },
  topCompanies: {
    labels: [],
    values: []
  }
});

const contractorSnapshots: Record<ContractorDashboardPeriod, DashboardSnapshot> = {
  daily: createEmptyContractorSnapshot(),
  weekly: createEmptyContractorSnapshot(),
  monthly: createEmptyContractorSnapshot(),
  yearly: createEmptyContractorSnapshot()
};

const initialState: ContractorDashboardState = {
  loading: false,
  period: "monthly",
  snapshots: contractorSnapshots
};

const getRows = (payload: any) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getLabel = (item: any) => (
  item?.label ??
  item?.name ??
  item?.nombre ??
  item?.name_company ??
  item?.name_company_client ??
  item?.company?.name ??
  item?.company_client?.name ??
  item?.user?.full_name ??
  "-"
);

const getValue = (item: any) => Number(
  item?.value ??
  item?.total_projects ??
  item?.projects ??
  item?.total ??
  item?.count ??
  0
);

const normalizeSales = (payload: any) => {
  const data = payload?.data ?? payload;
  const values = data?.net_premium ?? data?.values ?? data?.amount ?? [];

  return {
    summary: {
      totalPremium: Number(data?.total_net_premium ?? data?.total ?? 0),
      averagePremium: Number(data?.average_net_premium ?? data?.average ?? 0),
      growth: data?.growth ?? ""
    },
    premiumTrend: {
      labels: data?.labels ?? [],
      values
    }
  };
};

const normalizeTopCompanies = (payload: any) => {
  const data = payload?.data ?? payload;
  const chartData = data?.topCompanies ?? data?.companies ?? data;

  if (Array.isArray(chartData?.labels) && Array.isArray(chartData?.values)) {
    return {
      labels: chartData.labels,
      values: chartData.values
    };
  }

  const rows = getRows(chartData);
  return {
    labels: rows.map(getLabel),
    values: rows.map(getValue)
  };
};

const normalizePendingPolicies = (payload: any) => {
  const data = payload?.data ?? payload;
  const pendingData = data?.pendingPolicies ?? data?.pending_policies ?? data;

  if (
    Array.isArray(pendingData?.labels) &&
    (Array.isArray(pendingData?.pending) || Array.isArray(pendingData?.expired))
  ) {
    return {
      amount: Number(pendingData?.amount ?? pendingData?.total_amount ?? 0),
      labels: pendingData.labels,
      pending: pendingData.pending ?? [],
      expired: pendingData.expired ?? []
    };
  }

  return {
    amount: Number(pendingData?.amount ?? pendingData?.total_amount ?? pendingData?.total ?? 0),
    labels: ["Pólizas"],
    pending: [Number(pendingData?.pending ?? pendingData?.total_pending ?? 0)],
    expired: [Number(pendingData?.expired ?? pendingData?.total_expired ?? 0)]
  };
};

const normalizeCertificates = (payload: any) => {
  const data = payload?.data ?? payload;
  const stats = [
    { label: "Emitido", value: data?.issued?.total ?? 0, color: "#00CFE8" },
    { label: "Con factura", value: data?.withInvoice?.total ?? 0, color: "#7367F0" },
    { label: "Pagado", value: data?.withReference?.total ?? 0, color: "#20C997" },
    { label: "Cancelado", value: data?.cancelled?.total ?? 0, color: "#EA5455" },
    { label: "Comisionado", value: data?.commissioned?.total ?? 0, color: "#4b4b4b" }
  ];
  const total = data?.total?.total ?? data?.total ?? stats.reduce(
    (sum: number, item: { value: number }) => sum + item.value,
    0
  );

  return {
    total,
    growth: data?.growth ?? "",
    innerText: "Certificados emitidos",
    stats
  };
};

export const getContractorDashboard = createAsyncThunk(
  "contractorDashboard/get",
  async (params: Record<string, any> = {}) => {
    const response = await api.get("api/dashboard/contractors", { params });
    return {
      data: response.data
    };
  }
);

export const getContractorDashboardCertificates = createAsyncThunk(
  "contractorDashboard/certificates",
  async (params: Record<string, any> = {}, { getState }: any) => {
    const state = getState();
    const { productSelected } = state.products || {};
    const { businessLineSelected } = state.businessLines || {};
    const query = {
      period: "yearly",
      ...params,
      business_line: params.business_line ?? businessLineSelected?.id ?? 1,
      product: params.product ?? productSelected?.value ?? 3
    };
    const response = await api.get("api/dashboard/certificates", { params: query });
    return {
      data: response.data,
      period: query.period
    };
  }
);

export const getContractorDashboardSales = createAsyncThunk(
  "contractorDashboard/sales",
  async (params: Record<string, any> = {}, { getState }: any) => {
    const state = getState();
    const { productSelected } = state.products || {};
    const { businessLineSelected } = state.businessLines || {};
    const query = {
      period: "monthly",
      ...params,
      business_line: params.business_line ?? businessLineSelected?.id ?? 1,
      product: params.product ?? productSelected?.value ?? 3
    };
    const response = await api.get("api/dashboard/sales", { params: query });
    return {
      data: response.data,
      period: query.period
    };
  }
);

export const getContractorDashboardTopCompanies = createAsyncThunk(
  "contractorDashboard/topCompanies",
  async (params: Record<string, any> = {}) => {
    const query = {
      period: "monthly",
      ...params
    };
    const response = await api.get("api/dashboard_rc/top_companies", { params: query });
    return {
      data: response.data,
      period: query.period
    };
  }
);

export const getContractorDashboardPendingPolicies = createAsyncThunk(
  "contractorDashboard/pendingPolicies",
  async (params: Record<string, any> = {}) => {
    const query = {
      period: "yearly",
      ...params
    };
    const response = await api.get("api/dashboard_rc/pending_policies", { params: query });
    return {
      data: response.data,
      period: query.period
    };
  }
);

const contractorDashboardSlice = createSlice({
  name: "contractorDashboard",
  initialState,
  reducers: {
    setPeriod: (state, action) => {
      state.period = action.payload;
    },
    resetContractorDashboard: (state) => {
      state.loading = false;
      state.period = "monthly";
      state.snapshots = contractorSnapshots;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getContractorDashboard.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getContractorDashboard.fulfilled, (state, action) => {
      state.loading = false;
      const payload = action.payload?.data;
      if (payload?.period && payload?.snapshots) {
        state.period = payload.period;
        state.snapshots = payload.snapshots;
      }
    });
    builder.addCase(getContractorDashboard.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getContractorDashboardCertificates.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getContractorDashboardCertificates.fulfilled, (state, action) => {
      state.loading = false;
      const period = action.payload.period as ContractorDashboardPeriod;
      state.snapshots[period] = {
        ...state.snapshots[period],
        certificates: normalizeCertificates(action.payload.data)
      };
    });
    builder.addCase(getContractorDashboardCertificates.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getContractorDashboardSales.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getContractorDashboardSales.fulfilled, (state, action) => {
      state.loading = false;
      const period = action.payload.period as ContractorDashboardPeriod;
      const normalized = normalizeSales(action.payload.data);
      state.snapshots[period] = {
        ...state.snapshots[period],
        summary: {
          ...state.snapshots[period].summary,
          ...normalized.summary
        },
        premiumTrend: normalized.premiumTrend
      };
    });
    builder.addCase(getContractorDashboardSales.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getContractorDashboardTopCompanies.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getContractorDashboardTopCompanies.fulfilled, (state, action) => {
      state.loading = false;
      const period = action.payload.period as ContractorDashboardPeriod;
      state.snapshots[period] = {
        ...state.snapshots[period],
        topCompanies: normalizeTopCompanies(action.payload.data)
      };
    });
    builder.addCase(getContractorDashboardTopCompanies.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getContractorDashboardPendingPolicies.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getContractorDashboardPendingPolicies.fulfilled, (state, action) => {
      state.loading = false;
      const period = action.payload.period as ContractorDashboardPeriod;
      state.snapshots[period] = {
        ...state.snapshots[period],
        pendingPolicies: normalizePendingPolicies(action.payload.data)
      };
    });
    builder.addCase(getContractorDashboardPendingPolicies.rejected, (state) => {
      state.loading = false;
    });
  }
});

export const { setPeriod, resetContractorDashboard } = contractorDashboardSlice.actions;

export default contractorDashboardSlice.reducer;
